// src/pages/FunMoneyPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { formatCurrency, formatDate } from '../utils/formatting';
import LoadingSpinner from '../components/LoadingSpinner';

const FunMoneyPage = () => {
    const { loading, funMoneyData, funMoneySettings, addFunMoneyTransaction } = useAppContext();
    const [activeTab, setActiveTab] = useState('you'); // 'you' or 'spouse'

    // Form state for adding new transaction
    const [newTransaction, setNewTransaction] = useState({
        person: activeTab,
        description: '',
        amount: '',
        category: '',
        date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD
    });

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    if (loading || !funMoneyData) {
        return (
            <div className="flex items-center justify-center h-screen">
                <LoadingSpinner size="large" message="Loading fun money data..." />
            </div>
        );
    }

    const activePersonData = funMoneyData[activeTab];
    const categories = funMoneySettings?.categories || [
        'Dining Out',
        'Entertainment',
        'Shopping',
        'Hobbies',
        'Other'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewTransaction({
            ...newTransaction,
            [name]: name === 'amount' ? value : value,
            person: activeTab // Ensure person is always set to active tab
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);
        setSubmitting(true);

        try {
            // Validate form
            if (!newTransaction.description) {
                throw new Error('Please enter a description');
            }

            if (!newTransaction.amount || isNaN(parseFloat(newTransaction.amount))) {
                throw new Error('Please enter a valid amount');
            }

            // Convert amount to negative for spending
            const finalTransaction = {
                ...newTransaction,
                amount: parseFloat(newTransaction.amount) * -1 // Make it negative to represent spending
            };

            await addFunMoneyTransaction(finalTransaction);

            // Reset form
            setNewTransaction({
                person: activeTab,
                description: '',
                amount: '',
                category: '',
                date: new Date().toISOString().split('T')[0]
            });

            setSuccess(true);
        } catch (err) {
            setError(err.message || 'Failed to add transaction');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDepositAllowance = async () => {
        setError(null);
        setSuccess(false);
        setSubmitting(true);

        try {
            const allowanceTransaction = {
                person: activeTab,
                description: 'Monthly Allowance',
                amount: activePersonData.monthly_allowance,
                date: new Date().toISOString().split('T')[0]
            };

            await addFunMoneyTransaction(allowanceTransaction);
            setSuccess(true);
        } catch (err) {
            setError(err.message || 'Failed to deposit allowance');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-6 max-w-5xl">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Fun Money Tracker</h1>
                <Link
                    to="/"
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                    Back to Dashboard
                </Link>
            </div>

            {/* Tab navigation */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex -mb-px">
                    <button
                        className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'you'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        onClick={() => setActiveTab('you')}
                    >
                        Your Fun Money
                    </button>
                    <button
                        className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'spouse'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        onClick={() => setActiveTab('spouse')}
                    >
                        {funMoneyData.spouse.name}'s Fun Money
                    </button>
                </nav>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Summary card */}
                <div className="md:col-span-1">
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h2 className="text-xl font-semibold mb-4">
                            {activeTab === 'you' ? 'Your' : `${activePersonData.name}'s`} Balance
                        </h2>

                        <div className="text-3xl font-bold mb-2">{formatCurrency(activePersonData.current_balance)}</div>
                        <div className="text-gray-600 mb-4">of {formatCurrency(activePersonData.monthly_allowance)} monthly allowance</div>

                        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                            <div
                                className={`h-2.5 rounded-full ${activePersonData.current_balance > 0.75 * activePersonData.monthly_allowance
                                        ? 'bg-green-500'
                                        : activePersonData.current_balance > 0.25 * activePersonData.monthly_allowance
                                            ? 'bg-yellow-500'
                                            : 'bg-red-500'
                                    }`}
                                style={{ width: `${Math.min(100, (activePersonData.current_balance / activePersonData.monthly_allowance) * 100)}%` }}
                            ></div>
                        </div>

                        <button
                            onClick={handleDepositAllowance}
                            disabled={submitting}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                        >
                            {submitting ? 'Processing...' : `Deposit Monthly Allowance (${formatCurrency(activePersonData.monthly_allowance)})`}
                        </button>
                    </div>

                    {/* Add transaction form */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold mb-4">Add Transaction</h2>

                        {error && (
                            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
                                Transaction added successfully!
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                                    Description
                                </label>
                                <input
                                    type="text"
                                    id="description"
                                    name="description"
                                    value={newTransaction.description}
                                    onChange={handleInputChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    placeholder="Coffee shop, movie tickets, etc."
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="amount">
                                    Amount ($)
                                </label>
                                <input
                                    type="number"
                                    id="amount"
                                    name="amount"
                                    value={newTransaction.amount}
                                    onChange={handleInputChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    placeholder="12.50"
                                    step="0.01"
                                    min="0.01"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="category">
                                    Category
                                </label>
                                <select
                                    id="category"
                                    name="category"
                                    value={newTransaction.category}
                                    onChange={handleInputChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                >
                                    <option value="">Select a category</option>
                                    {categories.map((category) => (
                                        <option key={category} value={category}>{category}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    value={newTransaction.date}
                                    onChange={handleInputChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                            >
                                {submitting ? 'Adding...' : 'Add Transaction'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Transactions list */}
                <div className="md:col-span-2">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold mb-4">Recent Transactions</h2>

                        {activePersonData.transactions && activePersonData.transactions.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white">
                                    <thead>
                                        <tr>
                                            <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                                            <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                                            {/* Only show category if it exists in any transaction */}
                                            {activePersonData.transactions.some(tx => tx.category) && (
                                                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                                            )}
                                            <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activePersonData.transactions.map((transaction, index) => (
                                            <tr key={index}>
                                                <td className="py-2 px-4 border-b border-gray-200">
                                                    {formatDate(transaction.date)}
                                                </td>
                                                <td className="py-2 px-4 border-b border-gray-200">
                                                    {transaction.description}
                                                </td>
                                                {activePersonData.transactions.some(tx => tx.category) && (
                                                    <td className="py-2 px-4 border-b border-gray-200">
                                                        {transaction.category || '-'}
                                                    </td>
                                                )}
                                                <td className={`py-2 px-4 border-b border-gray-200 text-right font-mono ${transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                    {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                No transactions found.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FunMoneyPage;